import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
    addDraftOrderItem,
    fetchDraftOrderDetail,
    removeDraftOrderItem,
    updateDraftOrderItem,
} from "./thunk";
import { completeDraftOrder, deleteDraftOrder } from "../draftOrders/thunk";

interface DraftOrderDetailsState {
    data: Record<string, any>;
    loading: Record<string, boolean>;
    error: Record<string, string | null>;
}

const initialState: DraftOrderDetailsState = {
    data: {},
    loading: {},
    error: {},
};

const draftOrderDetailsSlice = createSlice({
    name: "draftOrderDetails",
    initialState,
    reducers: {
        upsertDraftOrderDetail: (
            state,
            action: PayloadAction<{ orderId: string | number; data: any }>,
        ) => {
            const key = String(action.payload.orderId);
            state.data[key] = action.payload.data;
            state.loading[key] = false;
            state.error[key] = null;
        },
        clearDraftOrderDetail: (state, action: PayloadAction<string | number>) => {
            const key = String(action.payload);
            delete state.data[key];
            delete state.loading[key];
            delete state.error[key];
        },
    },
    extraReducers: (builder) => {
        const setPending = (state: DraftOrderDetailsState, orderId: string | number) => {
            const key = String(orderId);
            state.loading[key] = true;
            state.error[key] = null;
        };
        const setRejected = (
            state: DraftOrderDetailsState,
            orderId: string | number,
            message?: string,
        ) => {
            const key = String(orderId);
            state.loading[key] = false;
            state.error[key] = message || null;
        };
        const setFulfilled = (
            state: DraftOrderDetailsState,
            orderId: string | number,
            data: any,
        ) => {
            const key = String(orderId);
            state.data[key] = data;
            state.loading[key] = false;
            state.error[key] = null;
        };

        builder.addCase(fetchDraftOrderDetail.pending, (state, action) => {
            setPending(state, action.meta.arg);
        });
        builder.addCase(fetchDraftOrderDetail.fulfilled, (state, action) => {
            setFulfilled(state, action.payload.orderId, action.payload.data);
        });
        builder.addCase(fetchDraftOrderDetail.rejected, (state, action) => {
            setRejected(state, action.meta.arg, action.error.message || "Không thể tải chi tiết đơn");
        });

        builder.addCase(updateDraftOrderItem.pending, (state, action) => {
            setPending(state, action.meta.arg.orderId);
        });
        builder.addCase(updateDraftOrderItem.fulfilled, (state, action) => {
            setFulfilled(state, action.payload.orderId, action.payload.data);
        });
        builder.addCase(updateDraftOrderItem.rejected, (state, action) => {
            setRejected(state, action.meta.arg.orderId, action.error.message || "Không thể cập nhật món");
        });

        builder.addCase(removeDraftOrderItem.pending, (state, action) => {
            setPending(state, action.meta.arg.orderId);
        });
        builder.addCase(removeDraftOrderItem.fulfilled, (state, action) => {
            setFulfilled(state, action.payload.orderId, action.payload.data);
        });
        builder.addCase(removeDraftOrderItem.rejected, (state, action) => {
            setRejected(state, action.meta.arg.orderId, action.error.message || "Không thể xóa món");
        });

        builder.addCase(addDraftOrderItem.pending, (state, action) => {
            setPending(state, action.meta.arg.orderId);
        });
        builder.addCase(addDraftOrderItem.fulfilled, (state, action) => {
            setFulfilled(state, action.payload.orderId, action.payload.data);
        });
        builder.addCase(addDraftOrderItem.rejected, (state, action) => {
            setRejected(state, action.meta.arg.orderId, action.error.message || "Không thể thêm món");
        });

        builder.addCase(completeDraftOrder.fulfilled, (state, action) => {
            const key = String(action.payload);
            delete state.data[key];
            delete state.loading[key];
            delete state.error[key];
        });
        builder.addCase(deleteDraftOrder.fulfilled, (state, action) => {
            const key = String(action.payload);
            delete state.data[key];
            delete state.loading[key];
            delete state.error[key];
        });
    },
});

export const { upsertDraftOrderDetail, clearDraftOrderDetail } = draftOrderDetailsSlice.actions;
export default draftOrderDetailsSlice.reducer;
